import { z } from "zod";

export const salesProviderSchema = z.enum(["stripe", "square"]);
export type SalesProvider = z.infer<typeof salesProviderSchema>;
export const salesModeSchema = z.enum(["live", "test"]);
export type SalesMode = z.infer<typeof salesModeSchema>;
export const saleStatusSchema = z.enum(["paid", "partially_refunded", "refunded", "authorized", "pending", "failed", "cancelled"]);
export type SaleStatus = z.infer<typeof saleStatusSchema>;

export const salesQuerySchema = z.object({
  provider: salesProviderSchema,
  mode: salesModeSchema.default("live"),
  from: z.string().datetime({ offset: true }),
  to: z.string().datetime({ offset: true }),
  cursor: z.string().min(1).max(12000).optional(),
}).strict().refine(q => Date.parse(q.from) >= 0 && Date.parse(q.from) < Date.parse(q.to), {
  message: "Choose a valid start and end date, starting in 1970 or later.",
});
export type SalesQuery = z.infer<typeof salesQuerySchema>;

/** Amounts are integer ISO currency minor units, normalized across providers. */
export interface Sale {
  id: string;
  provider: SalesProvider;
  reference: string;
  createdAt: string;
  currency: string;
  amountMinor: number;
  collectedMinor: number;
  refundedMinor: number;
  status: SaleStatus;
  disputed: boolean;
  customer: string | null;
  email: string | null;
  description: string | null;
  location: string | null;
  orderId: string | null;
  paymentMethod: string | null;
  receiptUrl: string | null;
}

export interface SalesPage {
  provider: SalesProvider;
  mode: SalesMode;
  status: "ready" | "not_configured" | "error";
  items: Sale[];
  nextCursor: string | null;
  message: string | null;
  fetchedAt: string;
}

export const SALE_STATUS_LABELS: Record<SaleStatus, string> = {
  paid: "Paid", partially_refunded: "Partially refunded", refunded: "Refunded",
  authorized: "Authorized", pending: "Pending", failed: "Failed", cancelled: "Cancelled",
};

export function currencyDigits(currency: string): number {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

export function formatSalesMoney(minor: number, currency: string): string {
  return new Intl.NumberFormat("en-AU", { style: "currency", currency, currencyDisplay: "code" })
    .format(minor / 10 ** currencyDigits(currency));
}
